import controlsHelper from './controlsHelper.jsx';
import * as PIXI from 'pixi.js';

export function createResizer() {
    let resizing = false;
    let currentControlName = null;
    let originalMousePos = { x: 0, y: 0 };
    let originalData = null;

    return {
        /**
         * Call this when the user begins a drag on a resize control.
         */
        startResize: (controlName, globalPos, textDesign) => {
            const parent = textDesign.parent;
            resizing = true;
            currentControlName = controlName;
            // Convert the global mouse position to the container's local coordinates.
            const localPos = parent.toLocal(new PIXI.Point(globalPos.x, globalPos.y));
            originalMousePos = { ...localPos };
            originalData = {
                x: textDesign.x,
                y: textDesign.y,
                scaleX: textDesign.scale.x,
                scaleY: textDesign.scale.y,
                width: textDesign.width,
                height: textDesign.height,
                rotation: textDesign.rotation,
            };
        },

        /**
         * Call this when the mouse moves while a resize is in progress.
         */
        updateResize: (globalPos, textDesign, updateControlPositionsCallback) => {
            if (!resizing || !currentControlName) return;
            const parent = textDesign.parent;
            // Convert the current global position to local coordinates.
            const localPos = parent.toLocal(new PIXI.Point(globalPos.x, globalPos.y));
            // Get the current zoom factor from the parent's scale (assumes uniform scaling).
            const zoomFactor = parent.scale.x;
            // Calculate the delta and adjust it for the zoom factor.
            const deltaX = (localPos.x - originalMousePos.x) / zoomFactor;
            const deltaY = (localPos.y - originalMousePos.y) / zoomFactor;
            if (currentControlName === 'rotate') {
                // Handle rotation.
                const center = { x: originalData.x, y: originalData.y };
                const startAngle = Math.atan2(originalMousePos.y - center.y, originalMousePos.x - center.x);
                const currentAngle = Math.atan2(localPos.y - center.y, localPos.x - center.x);
                const angleDelta = currentAngle - startAngle;
                textDesign.rotation = originalData.rotation + angleDelta;
            } else {
                // Handle corner controls (uniform scaling).
                const center = { x: originalData.x, y: originalData.y };
                const originalDistance = controlsHelper.getDistance(originalMousePos, center);
                const currentDistance = controlsHelper.getDistance(localPos, center);
                const scaleFactor = originalDistance ? currentDistance / originalDistance : 1;
                const newScale = originalData.scaleX * scaleFactor;
                textDesign.scale.x = newScale;
                textDesign.scale.y = newScale;
                const newHeight = originalData.height * scaleFactor;
                if (newHeight < textDesign.minSize || newHeight > textDesign.maxSize) {
                    // Optionally, set the scale to the clamped value before stopping.
                    const minScale = originalData.scaleX * (textDesign.minSize / originalData.height);
                    const maxScale = originalData.scaleX * (textDesign.maxSize / originalData.height);
                    const newScale = Math.max(minScale, Math.min(originalData.scaleX * scaleFactor, maxScale));
                    textDesign.scale.set(newScale, newScale);
                    // Stop further resizing.
                    resizing = false;
                    currentControlName = null;
                    return;
                }
            }

            if (typeof updateControlPositionsCallback === 'function') {
                updateControlPositionsCallback();
            }
        },

        /**
         * Call this when the user releases the mouse (ending the resize).
         */
        endResize: (textDesign) => {
            if (resizing) {
                resizing = false;
                currentControlName = null;
                return {
                    scaleX: textDesign.scale.x,
                    scaleY: textDesign.scale.y,
                    x: textDesign.x,
                    y: textDesign.y,
                    width: textDesign.width,
                    height: textDesign.height,
                    rotation: textDesign.rotation,
                };
            }
            return null;
        },

        /**
         * Returns whether a resize operation is currently in progress.
         */
        isResizing: () => resizing,
    };
}
